import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import softphone from '../services/softphone';
import { contactsAPI, statusAPI, callsAPI, orgDirectoryAPI } from '../services/api';
import { snapshotKnownIds, queueCallNote, resumePendingCallNotes } from '../services/callNotes';
import { useAuth } from '../hooks/useAuth';
import toast from 'react-hot-toast';

// Telefon holati butun yangi panel bo'ylab bitta joyda turadi: panel layout'ida
// bir marta ulanadi, sahifalar o'zgarganda SIP registratsiyasi UZILMAYDI.
//
// Muhim: JsSIP bilan ishlash mantig'i eski SoftphoneWidget'dan O'ZGARTIRISHSIZ
// ko'chirildi (callback'lar, ref orqali o'qish, izoh navbati, mikrofon
// diagnostikasi). Yangi narsa faqat ko'rinish va mijoz kartasi.

// TEZLIK UCHUN holat IKKI kontekstga bo'lingan:
//   PhoneContext      — kam o'zgaradigan holat (qo'ng'iroq holati, raqam, ro'yxatlar,
//                       funksiyalar). Ish joyi, mijoz kartasi, sarlavha shundan o'qiydi.
//   PhoneLiveContext  — tez o'zgaradigan holat: taymer (har soniya), mikrofon
//                       darajasi (sekundiga bir necha marta), terilayotgan raqam va
//                       yozilayotgan izoh (har harfda). Faqat telefon oroli va
//                       sozlamalar o'qiydi.
// Ilgari bitta kontekst edi: suhbat paytida butun ish joyi (200 tagacha qator)
// sekundiga ~5 marta qayta chizilib, kuchsiz kompyuterlarda panel qotardi.
//
// Sinovda soxta holat berish uchun eksport qilinadi (ko'rinish sinovlari).
export const PhoneContext = createContext(null);
export const PhoneLiveContext = createContext(null);

// Kam o'zgaradigan holat — ro'yxat va kartalar uchun.
export function usePhoneCore() {
  const ctx = useContext(PhoneContext);
  if (!ctx) throw new Error('usePhone faqat PhoneProvider ichida ishlaydi');
  return ctx;
}

// To'liq holat (tez o'zgaradiganlari bilan) — faqat telefon oroli va sozlamalar.
// Sinovda faqat PhoneContext berilsa, tez qiymatlar ham shu yerdan olinadi.
export function usePhone() {
  const core = usePhoneCore();
  const live = useContext(PhoneLiveContext);
  return live ? { ...core, ...live } : core;
}

export const isInternalNumber = (n) => /^1\d{2}$/.test(String(n || ''));

export const AUDIO_ISSUE_TITLES = {
  'mic-silent': "Mikrofondan ovoz kelmayapti — mijoz sizni ESHITMAYAPTI",
  'encoder-silent': "Mikrofon ishlayapti, lekin ovoz mijozga KETMAYAPTI",
  network: "Ovoz tarmoqqa ketmayapti — internetni tekshiring",
};

export const AUDIO_ISSUE_HINTS = {
  'mic-silent': "Mikrofon Windows sozlamalarida o'chirilgan yoki noto'g'ri qurilma tanlangan bo'lishi mumkin.",
  'encoder-silent': "Ulanish qayta tiklanmoqda. O'zgarmasa — mikrofonni almashtiring yoki qo'ng'iroqni qayta qiling.",
};

export function PhoneProvider({ children }) {
  const { user } = useAuth();

  // --- SIP va qo'ng'iroq ---
  const [registered, setRegistered] = useState(false);
  const [callState, setCallState] = useState('idle'); // idle | incoming | outgoing | active
  const [incomingCall, setIncomingCall] = useState(null);
  const [incomingHidden, setIncomingHidden] = useState(false);
  const [peerNumber, setPeerNumber] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactId, setContactId] = useState(null);
  const [callDuration, setCallDuration] = useState(0);
  const [ringSeconds, setRingSeconds] = useState(0);
  const [muted, setMuted] = useState(false);
  const [micLevel, setMicLevel] = useState(0);
  const [audioIssue, setAudioIssue] = useState(null);

  // --- operator navbat holati ---
  const [status, setStatus] = useState(user?.status || 'offline');

  // --- terilayotgan raqam ---
  const [dialNumber, setDialNumber] = useState('');

  // --- mijoz ma'lumoti ---
  // `peerCard` — joriy qo'ng'iroq uchun ICHKI ma'lumot (kontakt topildimi,
  // ismi nima): operator ko'rmaydi, telefon o'zi ishlatadi.
  // `card` — EKRANDA ko'rinadigan karta. U FAQAT operator jurnaldagi ismni
  // bosganda ochiladi — qo'ng'iroq kelganda o'zi ochilmaydi (shunday so'ralgan).
  const [peerCard, setPeerCard] = useState(null); // { number, loading, contact, calls[] }
  const [card, setCard] = useState(null);

  // --- izoh: qo'ng'iroq PAYTIDA yozilsa tugashi bilan saqlanadi ---
  const [liveNote, setLiveNote] = useState('');
  const [pendingNote, setPendingNote] = useState(null);

  // --- o'tkazish ---
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferOps, setTransferOps] = useState([]);
  const [transferLoading, setTransferLoading] = useState(false);
  const [transferring, setTransferring] = useState(false);

  // --- mikrofon qurilmalari ---
  const [micDevices, setMicDevices] = useState([]);
  const [micDeviceId, setMicDeviceId] = useState(softphone.getMicDeviceId());
  const [micPanelOpen, setMicPanelOpen] = useState(false);
  const [micBusy, setMicBusy] = useState(false);

  // --- jiringlash ovozi ---
  const [ringVolume, setRingVolumeState] = useState(softphone.getRingVolume());
  const [ringOutput, setRingOutputState] = useState(softphone.getRingOutput());
  const [ringOutputs, setRingOutputs] = useState([]);

  // --- ichki xodimlar ro'yxati (ichki qo'ng'iroq uchun) ---
  const [staff, setStaff] = useState([]);
  const [staffLoading, setStaffLoading] = useState(false);

  const timerRef = useRef(null);
  const incomingCallRef = useRef(null);
  const callInfoRef = useRef(null);
  const liveRef = useRef({ peer: '', name: '', state: 'idle' });
  const fnRef = useRef({});

  const isMicIssue = audioIssue === 'mic-silent' || audioIssue === 'encoder-silent';
  const inCall = callState === 'active' || callState === 'outgoing';
  const peerIsExternal = !!peerNumber && !isInternalNumber(peerNumber);

  const stopTimer = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  }, []);

  const startTimer = useCallback(() => {
    stopTimer();
    timerRef.current = setInterval(() => setCallDuration((p) => p + 1), 1000);
  }, [stopTimer]);

  // Qo'ng'iroq tugaganda: mijoz kartasi ATAYLAB tozalanmaydi — operator izoh
  // yozayotganda kim bilan gaplashganini ko'rib turishi kerak.
  const resetCallState = useCallback(() => {
    setCallState('idle');
    setIncomingCall(null);
    setIncomingHidden(false);
    incomingCallRef.current = null;
    setCallDuration(0);
    setMuted(false);
    setMicLevel(0);
    setAudioIssue(null);
    setPeerNumber('');
    setContactName('');
    setContactId(null);
    setTransferOpen(false);
    setTransferOps([]);
    setTransferring(false);
    stopTimer();
  }, [stopTimer]);

  // Mijoz konteksti: kontakt + shu raqam bilan oxirgi qo'ng'iroqlar.
  const fetchPeer = useCallback(async (number) => {
    const [cRes, lRes] = await Promise.all([
      contactsAPI.getByPhone(number).catch(() => null),
      callsAPI.list({ search: number, limit: 6 }).catch(() => null),
    ]);
    return {
      number,
      loading: false,
      contact: cRes?.data?.contact || null,
      calls: lRes?.data?.calls || [],
    };
  }, []);

  // Kontakt tashkilotga biriktirilgan bo'lsa — qarzini ALOHIDA yuklaymiz.
  // Kartani kutdirmaydi: ism darhol chiqadi, qarz bir zumdan keyin qo'shiladi.
  // Asosiy baza javob bermasa yoki server yangilanmagan bo'lsa (404) qarz
  // shunchaki ko'rsatilmaydi — qo'ng'iroq bunga bog'liq emas.
  const attachDebt = useCallback(async (num, contact) => {
    if (!contact || !contact.org_ref) return;
    try {
      const r = await orgDirectoryAPI.debt(contact.org_ref, contact.branch_userid);
      const merge = (prev) => (prev && prev.number === num ? { ...prev, debt: r.data } : prev);
      setPeerCard(merge);
      setCard(merge);
    } catch (e) { /* qarz ko'rsatilmaydi */ }
  }, []);

  const loadPeerCard = useCallback(async (number) => {
    if (!number || isInternalNumber(number)) { setPeerCard(null); return null; }
    setPeerCard({ number, loading: true, contact: null, calls: [] });
    const fresh = await fetchPeer(number);
    // Shu orada boshqa qo'ng'iroq boshlangan bo'lsa eski javobni yozmaymiz.
    setPeerCard((prev) => (prev && prev.number !== number ? prev : fresh));
    // Shu raqamning kartasi ochiq bo'lsa — uni ham yangilaymiz.
    setCard((prev) => (prev && prev.number === number ? fresh : prev));
    attachDebt(number, fresh.contact);
    return fresh;
  }, [fetchPeer, attachDebt]);

  // Jurnalda ismni bosganda ochiladigan karta.
  const openCard = useCallback(async (number) => {
    const num = String(number || '').trim();
    if (!num) return;
    setCard({ number: num, loading: true, contact: null, calls: [] });
    const fresh = await fetchPeer(num);
    setCard((prev) => (prev && prev.number !== num ? prev : fresh));
    attachDebt(num, fresh.contact);
  }, [fetchPeer, attachDebt]);

  const closeCard = useCallback(() => setCard(null), []);

  // === Izohni saqlash navbati (isbotlangan yo'l: cdrSync kechikishini kutadi) ===
  const sendNote = useCallback(async (n) => {
    const text = (n?.text || '').trim();
    if (!text || !user?.id) return;
    const tid = toast.loading('Izoh saqlanmoqda…');
    const knownIds = await Promise.resolve(n.knownIdsPromise).catch(() => null);
    queueCallNote(
      {
        userId: user.id, role: user.role, number: n.number, direction: n.direction,
        durationSec: n.durationSec, knownIds: knownIds || null, text,
      },
      {
        onSaved: () => {
          toast.success('Izoh saqlandi', { id: tid });
          window.dispatchEvent(new CustomEvent('cc:call-note-saved'));
        },
        onFailed: (item) => toast.error(
          `Izoh saqlanmadi — qo'ng'iroq yozuvi topilmadi. Jurnaldagi izoh tugmasi orqali qo'shing: «${item.text.slice(0, 80)}»`,
          { id: tid, duration: 15000 },
        ),
      },
    );
  }, [user]);

  // Qo'ng'iroq tugadi: gapirish paytida izoh yozilgan bo'lsa — darhol saqlanadi,
  // yozilmagan bo'lsa izoh kartasi ochiladi (majburiy emas).
  const finishCallNote = useCallback(() => {
    const info = callInfoRef.current;
    callInfoRef.current = null; // transferda onCallEnded ikki marta kelishi mumkin
    if (!info || info.transferred || !info.number || isInternalNumber(info.number)) {
      setLiveNote('');
      return;
    }
    const durationSec = Math.max(0, Math.round((Date.now() - info.answeredAt) / 1000));
    const typed = (liveNote || '').trim();
    if (typed) {
      sendNote({ ...info, durationSec, text: typed });
      setLiveNote('');
      return;
    }
    if (pendingNote && pendingNote.text.trim()) sendNote(pendingNote); // oldingisi yo'qolmasin
    setPendingNote({
      key: String(info.answeredAt),
      number: info.number,
      name: info.name,
      direction: info.direction,
      durationSec,
      knownIdsPromise: info.knownIdsPromise,
      text: '',
    });
  }, [liveNote, pendingNote, sendNote]);

  const savePendingNote = useCallback(() => {
    if (pendingNote) sendNote(pendingNote);
    setPendingNote(null);
  }, [pendingNote, sendNote]);

  // Callback'lar BIR MARTA yaratiladi va state'ning eski qiymatini ko'radi —
  // eng yangi funksiyalar ref orqali o'qiladi.
  useEffect(() => {
    fnRef.current = { finishCallNote, loadPeerCard };
  });

  useEffect(() => {
    liveRef.current = { peer: peerNumber, name: contactName, state: callState };
    const info = callInfoRef.current;
    if (info && contactName && info.number === peerNumber) info.name = contactName;
  }, [peerNumber, contactName, callState]);

  // Jiringlash sekundlari — "qancha vaqtdan beri kutyapti".
  useEffect(() => {
    if (callState !== 'incoming') { setRingSeconds(0); return undefined; }
    const startedAt = Date.now();
    setRingSeconds(0);
    const t = setInterval(() => setRingSeconds(Math.floor((Date.now() - startedAt) / 1000)), 1000);
    return () => clearInterval(t);
  }, [callState]);

  // Brauzerning avtomatik ovoz siyosati: sahifada biror narsa bosilmaguncha
  // AudioContext jim turadi va jiringlash eshitilmaydi.
  useEffect(() => {
    const unlock = () => softphone.unlockAudio();
    window.addEventListener('pointerdown', unlock);
    window.addEventListener('keydown', unlock);
    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
  }, []);

  // Chiqish qurilmalari — garnitura ulansa/uzilsa yangilanadi.
  useEffect(() => {
    let alive = true;
    const refresh = () => softphone.listAudioOutputs().then((l) => { if (alive) setRingOutputs(l); });
    refresh();
    const md = navigator.mediaDevices;
    if (md && md.addEventListener) md.addEventListener('devicechange', refresh);
    return () => {
      alive = false;
      if (md && md.removeEventListener) md.removeEventListener('devicechange', refresh);
    };
  }, []);

  // Sahifa yangilanganda tugamay qolgan izohlar davom ettiriladi.
  useEffect(() => {
    if (!user?.id) return;
    resumePendingCallNotes(user.id, {
      onSaved: () => toast.success('Izoh saqlandi'),
      onFailed: (item) => toast.error(`Izoh saqlanmadi: «${item.text.slice(0, 60)}»`, { duration: 12000 }),
    });
  }, [user?.id]);

  // === SIP ulanishi ===
  useEffect(() => {
    if (!user || !user.sip_extension) return undefined;

    const wsUrl = `wss://${window.location.host}/ws`;
    const sipDomain = window.location.hostname;
    const password = `Op3rator${user.sip_extension}WebRTC!`;

    softphone.connect({
      extension: user.sip_extension,
      password,
      wsUrl,
      sipDomain,
      onRegistered: () => {
        setRegistered(true);
        toast.success('Telefoniya tizimiga ulandi');
      },
      onUnregistered: (cause) => {
        setRegistered(false);
        if (cause) toast.error(`Telefoniya uzildi: ${cause}`);
      },
      onIncomingCall: async (call) => {
        setIncomingCall(call);
        incomingCallRef.current = call;
        setPeerNumber(call.from);
        setCallState('incoming');
        setIncomingHidden(false);
        setContactName('');
        setContactId(null);
        setPeerCard(null);
        const from = String(call.from || '');
        try {
          if (isInternalNumber(from)) {
            const res = await statusAPI.getAll();
            const op = (res.data.operators || []).find((o) => String(o.sip_extension) === from);
            if (op) setContactName(op.full_name);
            return;
          }
          const card = await fnRef.current.loadPeerCard(from);
          if (card?.contact?.name) {
            setContactName(card.contact.name);
            setContactId(card.contact.id);
          } else if (call.displayName && !/^Extension\s/i.test(call.displayName)) {
            setContactName(call.displayName);
          }
        } catch (e) {
          if (call.displayName && !/^Extension\s/i.test(call.displayName)) setContactName(call.displayName);
        }
      },
      onCallAnswered: () => {
        const live = liveRef.current;
        const number = live.peer;
        const external = !!number && !isInternalNumber(number);
        callInfoRef.current = {
          number,
          name: live.name,
          direction: live.state === 'incoming' ? 'inbound' : 'outbound',
          answeredAt: Date.now(),
          transferred: false,
          knownIdsPromise: external ? snapshotKnownIds(number) : Promise.resolve([]),
        };
        callInfoRef.current.knownIdsPromise.catch(() => {});
        setCallState('active');
        setIncomingCall(null);
        incomingCallRef.current = null;
        setCallDuration(0);
        startTimer();
      },
      onCallProgress: () => {
        setCallState((prev) => ((prev === 'outgoing' || prev === 'idle') ? 'outgoing' : prev));
      },
      onMicLevel: (lvl) => {
        setMicLevel((prev) => (Math.abs(prev - lvl) > 0.03 || (lvl === 0 && prev !== 0) ? lvl : prev));
      },
      onAudioIssue: (kind) => setAudioIssue(kind),
      onCallEnded: () => {
        resetCallState();
        fnRef.current.finishCallNote();
      },
      onCallFailed: (cause) => {
        callInfoRef.current = null;
        resetCallState();
        if (cause && cause !== 'Canceled' && cause !== 'Rejected') {
          toast.error(`Qo'ng'iroq amalga oshmadi: ${cause}`);
        }
      },
    });

    return () => {
      softphone.disconnect();
      stopTimer();
    };
  }, [user, resetCallState, startTimer, stopTimer]);

  // === Harakatlar ===
  const dial = useCallback(async (raw, name) => {
    const number = String(raw || '').trim();
    if (!number) return;
    if (!softphone.isRegistered()) { toast.error('Telefoniya ulanmagan'); return; }
    if (softphone.isInCall()) { toast.error("Boshqa qo'ng'iroq faol"); return; }
    try {
      await softphone.call(number);
      setCallState('outgoing');
      setPeerNumber(number);
      setContactName(name || '');
      setContactId(null);
      setLiveNote('');
      setPendingNote(null);
      if (isInternalNumber(number)) {
        setPeerCard(null);
      } else {
        const card = await fnRef.current.loadPeerCard(number);
        if (!name && card?.contact?.name) setContactName(card.contact.name);
        if (card?.contact?.id) setContactId(card.contact.id);
      }
    } catch (err) {
      toast.error(err.message || "Qo'ng'iroq qilib bo'lmadi");
      resetCallState();
    }
  }, [resetCallState]);

  // Boshqa sahifalardagi "qo'ng'iroq qilish" tugmalari shu event'ni yuboradi.
  useEffect(() => {
    const onTrigger = (e) => { if (e.detail?.number) dial(e.detail.number); };
    window.addEventListener('softphone:call', onTrigger);
    return () => window.removeEventListener('softphone:call', onTrigger);
  }, [dial]);

  const answer = useCallback(() => {
    const call = incomingCallRef.current;
    if (call) call.accept();
  }, []);

  const reject = useCallback(() => {
    const call = incomingCallRef.current;
    if (call) call.reject();
    resetCallState();
  }, [resetCallState]);

  // Yashirish = rad etish EMAS: SIP'ga hech narsa yuborilmaydi, qo'ng'iroq
  // navbatda qolaveradi. Faqat ovoz o'chadi.
  const hideIncoming = useCallback(() => {
    setIncomingHidden(true);
    softphone.silenceRing();
  }, []);

  const showIncoming = useCallback(() => setIncomingHidden(false), []);

  const hangup = useCallback(() => {
    softphone.hangup();
    resetCallState();
  }, [resetCallState]);

  const toggleMute = useCallback(() => {
    setMuted((prev) => { softphone.mute(!prev); return !prev; });
  }, []);

  const pressDigit = useCallback((d) => {
    if (callState === 'active') softphone.sendDTMF(d);
    else if (callState === 'idle') setDialNumber((p) => p + d);
  }, [callState]);

  const openTransfer = useCallback(async () => {
    setTransferOpen(true);
    setTransferLoading(true);
    try {
      const res = await statusAPI.availableOperators();
      setTransferOps(res.data.operators || []);
    } catch (e) {
      toast.error("Operatorlar ro'yxatini olishda xato");
      setTransferOps([]);
    } finally {
      setTransferLoading(false);
    }
  }, []);

  const doTransfer = useCallback((op) => {
    setTransferring(true);
    softphone.transfer(op.sip_extension, {
      onSuccess: () => {
        if (callInfoRef.current) callInfoRef.current.transferred = true;
        toast.success(`Qo'ng'iroq ${op.full_name}ga o'tkazildi`);
      },
      onError: (cause) => {
        setTransferring(false);
        setTransferOpen(false);
        toast.error(`O'tkazib bo'lmadi: ${cause || "noma'lum"}. Qo'ng'iroq sizda qoldi.`);
      },
    });
  }, []);

  const loadStaff = useCallback(async () => {
    setStaffLoading(true);
    try {
      const res = await statusAPI.availableOperators();
      setStaff(res.data.operators || []);
    } catch (e) {
      toast.error("Xodimlar ro'yxatini olishda xato");
      setStaff([]);
    } finally {
      setStaffLoading(false);
    }
  }, []);

  // Panel ochilganda avtomatik onlayn (queue ga qo'shish/unpause) — lekin
  // TANAFFUSNI buzmaymiz. Faqat bir marta, jim: toast chiqarmaydi.
  useEffect(() => {
    if (!user?.sip_extension) return;
    if ((user.status || '') === 'break') return;
    statusAPI.update('online').then(() => setStatus('online')).catch(() => {});
  }, [user]);

  const changeStatus = useCallback(async (next) => {
    try {
      await statusAPI.update(next);
      setStatus(next);
      const labels = { online: 'Onlayn', break: 'Tanaffus', offline: 'Oflayn' };
      toast.success(`Holat: ${labels[next] || next}`);
    } catch (e) {
      toast.error('Holatni o\'zgartirib bo\'lmadi');
    }
  }, []);

  // === Mikrofon ===
  const openMicPanel = useCallback(async (withTest = false) => {
    setMicPanelOpen(true);
    try {
      // Qurilma NOMLARI faqat ruxsatdan keyin ko'rinadi — bo'sh holatda
      // avval mikrofonni yoqamiz (test rejimi).
      if (withTest) await softphone.startMicTest();
    } catch (e) {
      toast.error(e.message || "Mikrofonni ochib bo'lmadi");
    }
    setMicDevices(await softphone.listMicDevices());
    setMicDeviceId(softphone.getMicDeviceId());
  }, []);

  const closeMicPanel = useCallback(() => {
    setMicPanelOpen(false);
    softphone.stopMicTest(); // qo'ng'iroq ketayotgan bo'lsa tegmaydi
    setMicLevel(0);
  }, []);

  const chooseMic = useCallback(async (deviceId) => {
    setMicBusy(true);
    try {
      await softphone.setMicDevice(deviceId);
      setMicDeviceId(deviceId);
      setMicDevices(await softphone.listMicDevices());
      toast.success('Mikrofon almashtirildi');
    } catch (e) {
      toast.error(e.message || 'Mikrofonni almashtirishda xato');
    } finally {
      setMicBusy(false);
    }
  }, []);

  // Test rejimi ochiq qolib mikrofon behuda yonib turmasin.
  useEffect(() => {
    if (!micPanelOpen) return undefined;
    const t = setTimeout(() => {
      if (!softphone.isInCall()) {
        softphone.stopMicTest();
        setMicLevel(0);
        setMicPanelOpen(false);
      }
    }, 90000);
    return () => clearTimeout(t);
  }, [micPanelOpen]);

  const testRing = useCallback(() => softphone.testRing(), []);
  const [supportsRingOutput] = useState(() => softphone.supportsRingOutput());

  const setRingVolume = useCallback((v) => {
    setRingVolumeState(v);
    softphone.setRingVolume(v);
  }, []);

  // Qo'shimcha chiqish: quloqchin stolda yotgan bo'lsa ham eshitilsin uchun
  // jiringlash kompyuter karnayiga HAM yuboriladi (asosiy qurilma o'rniga emas).
  const setRingOutput = useCallback((deviceId) => {
    setRingOutputState(deviceId);
    softphone.setRingOutput(deviceId);
    if (deviceId) setTimeout(() => softphone.testRing(), 250);
  }, []);

  // useMemo: qiymat FAQAT ro'yxatdagi holatlardan biri o'zgarganda yangilanadi.
  // Funksiyalar useCallback bilan barqaror, shuning uchun ular qiymatni yangilamaydi.
  const core = useMemo(() => ({
    user,
    registered, callState, inCall, incomingCall, incomingHidden,
    peerNumber, contactName, contactId, setContactName, setContactId,
    muted, audioIssue, isMicIssue, peerIsExternal,
    status, changeStatus,
    peerCard, loadPeerCard, card, openCard, closeCard,
    transferOpen, setTransferOpen, transferOps, transferLoading, transferring, openTransfer, doTransfer,
    micDevices, micDeviceId, micPanelOpen, micBusy, openMicPanel, closeMicPanel, chooseMic,
    ringVolume, setRingVolume, ringOutput, setRingOutput, ringOutputs,
    staff, staffLoading, loadStaff,
    dial, answer, reject, hideIncoming, showIncoming, hangup, toggleMute, pressDigit,
    testRing, supportsRingOutput,
  }), [
    user, registered, callState, inCall, incomingCall, incomingHidden,
    peerNumber, contactName, contactId,
    muted, audioIssue, isMicIssue, peerIsExternal,
    status, changeStatus,
    peerCard, loadPeerCard, card, openCard, closeCard,
    transferOpen, transferOps, transferLoading, transferring, openTransfer, doTransfer,
    micDevices, micDeviceId, micPanelOpen, micBusy, openMicPanel, closeMicPanel, chooseMic,
    ringVolume, setRingVolume, ringOutput, setRingOutput, ringOutputs,
    staff, staffLoading, loadStaff,
    dial, answer, reject, hideIncoming, showIncoming, hangup, toggleMute, pressDigit,
    testRing, supportsRingOutput,
  ]);

  const live = useMemo(() => ({
    callDuration, ringSeconds, micLevel,
    dialNumber, setDialNumber,
    liveNote, setLiveNote, pendingNote, setPendingNote, savePendingNote,
  }), [callDuration, ringSeconds, micLevel, dialNumber, liveNote, pendingNote, savePendingNote]);

  return (
    <PhoneContext.Provider value={core}>
      <PhoneLiveContext.Provider value={live}>{children}</PhoneLiveContext.Provider>
    </PhoneContext.Provider>
  );
}
